/**
 * Profile Past Performances Page
 * List and manage past performances with add/edit/delete
 * Uses past performances store and artist API
 *
 * @author Varun Pandey
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { CalendarRange, ArrowLeft, Plus, X, Hash, ListMusic, Star } from 'lucide-react';
import { Input } from '../../components/ui/Input';
import { Select, type SelectOption } from '../../components/ui/Select';
import type { PastPerformanceApiItem } from '@/services/artistService';
import { usePastPerformancesStore } from '@/stores/pastPerformancesStore';
import { useArtistDetailsStore } from '@/stores/artistDetailsStore';
import { Loader } from '@/components/ui';
import { ProfileCompletionBlocksControl } from '@/components/profile/ProfileCompletionBlocksControl';
import ArtistPastPerformances from './website-preview/Free/components/ArtistPastPerformances';
import ArtistEconomyPerformance from './website-preview/Economy/components/ArtistEconomyPerformance';
import ArtistPremiumPerformanceTable from './website-preview/Premium/components/ArtistPremiumPerformanceTable';
import ArtistElitePerformance from './website-preview/Elite/components/ArtistElitePerformance';
import { cn } from '../../utils/cn';

type PlanKey = 'free' | 'economy' | 'premium' | 'elite';

function subscriptionNameToPlanKey(subscriptionName: string | null | undefined): PlanKey | null {
  if (!subscriptionName) return null;
  const n = subscriptionName.toLowerCase().trim();
  if (!n) return null;
  if (n.includes('limited')) return 'free';
  if (n.includes('elite')) return 'elite';
  if (n.includes('premium')) return 'premium';
  if (n.includes('economy')) return 'economy';
  if (n.includes('free')) return 'free';
  return null;
}

export const EventName = [
  { id: 1, event_name: 'Campus', key_name: 'campus', e_slug: 'campus' },
  { id: 2, event_name: 'Charity', key_name: 'charity', e_slug: 'charity' },
  { id: 3, event_name: 'Concert/Festival', key_name: 'concert_festivals', e_slug: 'concert-festival' },
  { id: 4, event_name: 'Corporate', key_name: 'corporate', e_slug: 'corporate' },
  { id: 5, event_name: 'Exhibition', key_name: 'exhibitions', e_slug: 'exhibition' },
  { id: 6, event_name: 'Fashion Show', key_name: 'fashion', e_slug: 'fashion-show' },
  { id: 7, event_name: 'Inauguration', key_name: 'inaugurations', e_slug: 'inauguration' },
  { id: 8, event_name: 'Kids Party', key_name: 'kid_parties', e_slug: 'kids-party' },
  { id: 9, event_name: 'Photo/Video Shoot', key_name: 'photo_video_shoots', e_slug: 'photo-video-shoot' },
  { id: 10, event_name: 'Private Party', key_name: 'private_parties', e_slug: 'private-party' },
  { id: 11, event_name: 'Professional Hiring', key_name: 'professional_hiring', e_slug: 'professional-hiring' },
  { id: 12, event_name: 'Religious', key_name: 'religious_functions', e_slug: 'religious' },
  { id: 13, event_name: 'Restaurant', key_name: 'restaurants_pubs_bars', e_slug: 'restaurant' },
  { id: 16, event_name: 'Video Shoutout', key_name: 'video_shoutout', e_slug: 'video-shoutout' },
  { id: 15, event_name: 'Virtual', key_name: 'virtual', e_slug: 'virtual' },
  { id: 14, event_name: 'Wedding', key_name: 'weddings', e_slug: 'wedding' },
];

const EVENT_OPTIONS: SelectOption[] = EventName.map((e) => ({
  value: String(e.id),
  label: e.event_name,
}));

const POPULAR_MAX_LENGTH = 100;

interface PerformanceEntry {
  id: string;
  backendId?: number;
  event_count: string;
  event_type: string;
  event_name?: string;
  desc: string;
}

function mapStoreDataToEntries(items: PastPerformanceApiItem[] | null): PerformanceEntry[] {
  if (!items?.length) return [];
  return items.map((item) => ({
    id: `pp-${item.id}`,
    backendId: item.id,
    event_count: String(item.event_count),
    event_type: String(item.event_type),
    event_name: item.event_name,
    desc: item.desc ?? '',
  }));
}

export const ProfilePastPerformancesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const storeData = usePastPerformancesStore((s) => s.data);
  const isLoading = usePastPerformancesStore((s) => s.isLoading);
  const ensureFetched = usePastPerformancesStore((s) => s.ensureFetched);
  const addPerformances = usePastPerformancesStore((s) => s.addPerformances);
  const deleteItem = usePastPerformancesStore((s) => s.deleteItem);
  const setStoreError = usePastPerformancesStore((s) => s.setError);

  const [entries, setEntries] = useState<PerformanceEntry[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveLoading, setSaveLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const subscriptionName = useArtistDetailsStore(
    (s) => s.data?.subscriptions.subscription_name,
  );
  const ensureArtistDetailsFetched = useArtistDetailsStore((s) => s.ensureFetched);
  const planParam = (searchParams.get('plan') ?? '').toLowerCase() as PlanKey | '';
  const subscriptionPlan = useMemo(
    () => subscriptionNameToPlanKey(subscriptionName),
    [subscriptionName],
  );
  const plan: PlanKey = (planParam && ['free', 'economy', 'premium', 'elite'].includes(planParam)
    ? planParam
    : subscriptionPlan ?? 'free') as PlanKey;
  const planPreviewLabel = plan === 'free' ? 'LIMITED' : plan;

  useEffect(() => {
    ensureFetched();
  }, [ensureFetched]);

  useEffect(() => {
    ensureArtistDetailsFetched().catch(() => undefined);
  }, [ensureArtistDetailsFetched]);

  useEffect(() => {
    setEntries(mapStoreDataToEntries(storeData));
  }, [storeData]);

  const storeError = usePastPerformancesStore((s) => s.error);
  useEffect(() => {
    if (storeError) {
      toast.error(storeError);
      setStoreError(null);
    }
  }, [storeError, setStoreError]);

  const addEntry = () => {
    setEntries((prev) => [
      ...prev,
      {
        id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        event_count: '',
        event_type: '',
        desc: '',
      },
    ]);
  };

  const removeEntry = async (id: string) => {
    const entry = entries.find((e) => e.id === id);
    if (entry?.backendId != null) {
      setDeletingId(id);
      try {
        await deleteItem(entry.backendId);
        toast.success('Performance removed');
      } catch {
        // Store sets error; useEffect toasts it
      } finally {
        setDeletingId(null);
      }
    } else {
      setEntries((prev) => prev.filter((e) => e.id !== id));
    }
  };

  const cancelNew = () => {
    setEntries((prev) => prev.filter((e) => e.backendId != null));
    setErrors({});
  };

  const handleSave = async () => {
    const newEntries = entries.filter((e) => e.backendId == null);
    const newErrors: Record<string, string> = {};

    newEntries.forEach((entry) => {
      const base = `pp-${entry.id}`;
      if (!entry.event_count.trim()) newErrors[`${base}-total`] = 'Total events is required';
      else if (entry.event_count.length > 4) newErrors[`${base}-total`] = 'Max 4 digits';
      if (!entry.event_type) newErrors[`${base}-primary`] = 'Primary event type is required';
      if (!entry.desc.trim()) newErrors[`${base}-popular`] = 'Popular ones is required';
    });

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      const firstKey = Object.keys(newErrors)[0];
      toast.error(newErrors[firstKey]);
      return;
    }

    try {
      setSaveLoading(true);
      const payload = newEntries.map((e) => ({
        event_count: parseInt(e.event_count, 10),
        event_type: parseInt(e.event_type, 10),
        desc: e.desc.slice(0, POPULAR_MAX_LENGTH),
      }));
      await addPerformances(payload);
      toast.success('Past performances saved');
    } catch {
      toast.error('Failed to save past performances.');
    } finally {
      setSaveLoading(false);
    }
  };

  const hasNewEntries = entries.some((e) => e.backendId == null);
  const savedEntries = entries.filter((e) => e.backendId != null);
  const newEntries = entries.filter((e) => e.backendId == null);

  return (
    <div className="w-full bg-[#0B0B0D] text-white">
      <div className="w-full mx-auto md:px-4 md:py-6 lg:py-8 pb-16">
        <header className="mb-6 lg:mb-8">
          <div className="mb-5 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex min-h-[44px] shrink-0 items-center gap-2 -ml-2 rounded-xl pl-2 pr-3 text-[#8E8E93] transition-colors hover:bg-[#1C1C1E] hover:text-white active:bg-[#2C2C2E] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F33363] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0D]"
            >
              <ArrowLeft className="h-5 w-5 shrink-0" />
              <span className="text-sm font-medium">Back</span>
            </button>
            <ProfileCompletionBlocksControl />
          </div>
          <div className="flex items-center gap-3 mb-2">
            <div className="flex h-10 w-10 lg:h-12 lg:w-12 shrink-0 items-center justify-center rounded-xl bg-[#1C1C1E] border border-[#3A3A3C]">
              <CalendarRange className="h-5 w-5 lg:h-6 lg:w-6 text-[#8E8E93]" />
            </div>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-white tracking-tight uppercase tracking-widest">
                Past Performances
              </h1>
              <p className="text-[#8E8E93] text-sm lg:text-base mt-0.5">
                Showcase your overall experience and most memorable events
              </p>
            </div>
          </div>
        </header>
        {!hasNewEntries && (
          <div className="mb-4 rounded-xl border border-[#2C2C2E] bg-[#141416] px-4 py-3 text-xs text-[#A0A0A4] flex items-center justify-between gap-3">
            <span>
              This is a live preview of how your Past Performances section will appear on your public website.
            </span>
            <span className="inline-flex items-center rounded-full px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-[#E5E5EA]">
              Plan: {planPreviewLabel}
            </span>
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
          className="space-y-6"
        >
          <section className="rounded-2xl space-y-4">
            {!hasNewEntries ? (
              <div className="flex flex-row items-end justify-end gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={addEntry}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#151517] border border-[#3A3A3C] px-3 py-1.5 text-[11px] text-[#F2F2F7] hover:bg-[#2C2C2E] transition-colors text-nowrap"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add performance
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={addEntry}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#151517] border border-[#3A3A3C] px-3 py-1.5 text-[11px] text-[#F2F2F7] hover:bg-[#2C2C2E] transition-colors text-nowrap"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add more
                </button>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={isLoading}
                    className="relative inline-flex items-center gap-1.5 rounded-full bg-white text-black border border-[#3A3A3C] px-3 py-1.5 text-[11px] font-medium hover:opacity-90 transition-colors text-nowrap disabled:opacity-50"
                  >
                    {saveLoading && (
                      <span className="absolute inset-0 flex items-center justify-center">
                        <Loader size="small" />
                      </span>
                    )}
                    <span className={saveLoading ? 'opacity-0' : ''}>Save</span>
                  </button>
                  <button
                    type="button"
                    onClick={cancelNew}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#151517] border border-[#3A3A3C] px-3 py-1.5 text-[11px] text-[#F2F2F7] hover:bg-[#2C2C2E] transition-colors text-nowrap"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {isLoading && entries.length === 0 ? (
              <div className="flex items-center justify-center min-h-[80px]">
                <Loader size="small" />
              </div>
            ) : (
              <>
                {hasNewEntries ? (
                  <div className="space-y-4">
                    {newEntries.map((entry, index) => {
                      const base = `pp-${entry.id}`;
                      return (
                        <div
                          key={entry.id}
                          className="rounded-2xl border border-[#3A3A3C] px-4 py-4 space-y-3"
                        >
                          <p className="text-xs font-medium text-[#8E8E93]">#{index + 1}</p>
                          <div className="grid gap-3 md:grid-cols-3">
                            <div>
                              <label className="mb-2 flex items-center gap-2 text-sm font-medium text-[#8E8E93]">
                                <Hash className="h-4 w-4 shrink-0" />
                                Total Events Performed
                              </label>
                              <Input
                                value={entry.event_count}
                                onChange={(e) => {
                                  const v = e.target.value.replace(/\D/g, '').slice(0, 4);
                                  setEntries((prev) =>
                                    prev.map((p) =>
                                      p.id === entry.id ? { ...p, event_count: v } : p
                                    )
                                  );
                                }}
                                inputMode="numeric"
                                placeholder="e.g. 100"
                                error={errors[`${base}-total`]}
                                hideErrorText
                              />
                            </div>
                            <div>
                              <label className="mb-2 flex items-center gap-2 text-sm font-medium text-[#8E8E93]">
                                <ListMusic className="h-4 w-4 shrink-0" />
                                Primary Event Type
                              </label>
                              <Select
                                options={EVENT_OPTIONS}
                                value={entry.event_type || undefined}
                                onChange={(value) =>
                                  setEntries((prev) =>
                                    prev.map((p) =>
                                      p.id === entry.id ? { ...p, event_type: value } : p
                                    )
                                  )
                                }
                                placeholder="Select event type"
                                error={errors[`${base}-primary`]}
                                hideErrorText
                              />
                            </div>
                            <div className="space-y-1">
                              <div className="flex items-center justify-between">
                                <label className="flex items-center gap-2 text-sm font-medium text-[#8E8E93]">
                                  <Star className="h-4 w-4 shrink-0" />
                                  Popular Ones
                                </label>
                                <span className="text-xs text-[#8E8E93]">
                                  {entry.desc.length}/{POPULAR_MAX_LENGTH}
                                </span>
                              </div>
                              <input
                                value={entry.desc}
                                onChange={(e) =>
                                  setEntries((prev) =>
                                    prev.map((p) =>
                                      p.id === entry.id
                                        ? { ...p, desc: e.target.value.slice(0, POPULAR_MAX_LENGTH) }
                                        : p
                                    )
                                  )
                                }
                                maxLength={POPULAR_MAX_LENGTH}
                                placeholder="Your most talked-about events"
                                className={cn(
                                  'flex h-12 w-full rounded-[0.75rem] bg-transparent border border-[#3A3A3C] px-4 py-3 text-sm text-white placeholder:text-[#8E8E93] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0D] disabled:opacity-50',
                                  errors[`${base}-popular`] ? 'border-destructive ring-2 ring-destructive' : 'border-[#3A3A3C]'
                                )}
                              />
                              {errors[`${base}-popular`] && (
                                <p className="text-xs text-destructive">{errors[`${base}-popular`]}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : null}

                {!hasNewEntries && savedEntries.length > 0 && (
                  <div className="rounded-2xl px-1 py-4 lg:px-2 lg:py-6">
                    <AnimatePresence mode="wait">
                      {subscriptionName === 'Elite' && (
                        <motion.div
                          key="elite"
                          initial={{ opacity: 0, y: 12, scale: 0.98 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -10, scale: 0.98 }}
                          transition={{
                            duration: 0.35,
                            ease: [0.25, 0.1, 0.25, 1],
                          }}
                          className='-mt-8'
                        >
                          <ArtistElitePerformance
                            hideHero
                            hidePastHeading
                            hideImage
                            enableDelete
                          />
                        </motion.div>
                      )}

                      {subscriptionName === 'Premium' && (
                        <motion.div
                          key="premium"
                          initial={{ opacity: 0, y: 12, scale: 0.98 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -10, scale: 0.98 }}
                          transition={{
                            duration: 0.35,
                            ease: [0.25, 0.1, 0.25, 1],
                          }}
                        >
                          <ArtistPremiumPerformanceTable hideTitle enableDelete />
                        </motion.div>
                      )}

                      {subscriptionName === 'Economy' && (
                        <motion.div
                          key="economy"
                          initial={{ opacity: 0, y: 12, scale: 0.98 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -10, scale: 0.98 }}
                          transition={{
                            duration: 0.35,
                            ease: [0.25, 0.1, 0.25, 1],
                          }}
                          className='flex flex-col items-center justify-center'
                        >
                          <ArtistEconomyPerformance hideDetails hidePastHeading enableDelete />
                        </motion.div>
                      )}

                      {(subscriptionName === 'Free' || !subscriptionName) && (
                        <motion.div
                          key="free"
                          initial={{ opacity: 0, y: 12, scale: 0.98 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -10, scale: 0.98 }}
                          transition={{
                            duration: 0.35,
                            ease: [0.25, 0.1, 0.25, 1],
                          }}
                          className='md:-mx-10 lg:-mx-20'
                        >
                          <ArtistPastPerformances hideTitle enableDelete />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}

                {!hasNewEntries && savedEntries.length === 0 && !isLoading && (
                  <p className="text-sm text-[#8E8E93] py-4 text-center ">
                    No past performances yet. Click &quot;Add performance&quot; to add one.
                  </p>
                )}
              </>
            )}
          </section>
        </motion.div>
      </div>
    </div>
  );
};
